/**
 * Logged-in Console / Logout actions for public marketing headers.
 * Console returns the user to their current workspace dashboard.
 */

import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { jwtDecode } from 'jwt-decode'
import type { RootState } from '@/app/store'
import { tokenManager } from '@/utils/tokenManager'
import { authService } from '@/services/api/shared/auth.service'
import { socketService } from '@/services/realtime/socket.service'
import { UrlGenerator } from '@/utils/url-generator.util'
import { getStoredContext } from '@/utils/context.util'
import { buildCanonicalUrl, isValidContextForUrl } from '@/utils/canonical-url.util'
import { useUIPreferences } from '@/contexts/UIPreferencesContext'
import { navigationService } from '@/services/api/shared/navigation.service'

type ContextTokenPayload = {
  contextType?: string
  organizationSlug?: string
  branchSlug?: string
}

function dashboardUrlFromType(
  contextType: string | undefined,
  organizationSlug?: string,
  branchSlug?: string
): string | null {
  const type = (contextType || '').toLowerCase()
  try {
    if (type === 'platform') {
      return buildCanonicalUrl({ type: 'PLATFORM' })
    }
    if (type === 'organization' && organizationSlug) {
      return buildCanonicalUrl({ type: 'ORGANIZATION', orgSlug: organizationSlug })
    }
    if (type === 'branch' && organizationSlug && branchSlug) {
      return buildCanonicalUrl({ type: 'BRANCH', orgSlug: organizationSlug, branchSlug })
    }
  } catch {
    return null
  }
  return null
}

function resolveDashboardUrl(contextToken: string | null): string | null {
  const stored = getStoredContext()
  if (stored && isValidContextForUrl(stored)) {
    return buildCanonicalUrl(stored)
  }

  if (contextToken) {
    try {
      const payload = jwtDecode<ContextTokenPayload>(contextToken)
      const fromToken = dashboardUrlFromType(
        payload.contextType,
        payload.organizationSlug,
        payload.branchSlug
      )
      if (fromToken) return fromToken
    } catch {
      // Token may be opaque or expired; fall through.
    }
  }

  return null
}

export function usePublicSessionNav(options?: { probeCookieSession?: boolean }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { setUIPreferences } = useUIPreferences()
  const reduxAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated)
  const probeCookieSession = options?.probeCookieSession === true

  const [hasContextToken, setHasContextToken] = useState(
    () => !!tokenManager.getToken('contextToken')
  )
  const [hasValidSession, setHasValidSession] = useState(false)

  useEffect(() => {
    setHasContextToken(!!tokenManager.getToken('contextToken'))
  }, [location.pathname, location.search])

  useEffect(() => {
    if (!probeCookieSession) return
    if (hasContextToken || reduxAuthenticated) return
    if (location.pathname.startsWith('/login')) return
    let cancelled = false
    authService
      .refreshToken()
      .then(() => {
        if (!cancelled) setHasValidSession(true)
      })
      .catch(() => {
        if (!cancelled) setHasValidSession(false)
      })
    return () => {
      cancelled = true
    }
  }, [probeCookieSession, hasContextToken, reduxAuthenticated, location.pathname, location.search])

  const isLoggedIn = hasContextToken || reduxAuthenticated || hasValidSession

  const handleLogout = useCallback(async () => {
    try {
      socketService.disconnect()
      setUIPreferences(null)
      await authService.logout()
    } catch {
      // Continue with cleanup even if the API call fails.
    }
    tokenManager.clearAllAuthData()
    sessionStorage.clear()
    window.location.href = UrlGenerator.getBaseLogoutUrl()
  }, [setUIPreferences])

  const handleConsoleClick = useCallback(async () => {
    const contextToken = tokenManager.getToken('contextToken')
    const dashboardUrl = resolveDashboardUrl(contextToken)
    if (dashboardUrl) {
      window.location.href = dashboardUrl
      return
    }

    if (contextToken) {
      navigate('/select-context')
      return
    }

    try {
      await authService.refreshToken()
      const userContextResponse = await navigationService.getUserContext(true)
      const accessibleDomains = userContextResponse?.domainNavigation?.accessibleDomains || []
      if (accessibleDomains.length === 0) {
        navigate('/onboarding?step=organization-profile&resume=true')
        return
      }
      navigate('/select-context')
    } catch {
      navigate('/login')
    }
  }, [navigate])

  return {
    isLoggedIn,
    handleConsoleClick,
    handleLogout,
  }
}
